import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
  Autocomplete,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import AppButton from "../../common/AppButton";
import { useAppDispatch } from "../../../app/hooks";
import { addCategoryInitialValues } from "../../forms/initialValues";
import { Formik, Form, Field } from "formik";
import {
  AddCategory,
  EditedCategory,
} from "../../../features/category/categorySlice";

const typeOptions = [
  { label: "Medical", value: 0 },
  { label: "Non-Medical", value: 1 },
];

interface IProps {
  handleClose: () => void;
  setOpenSnackbar: () => void;
  getCategory: () => void;
  editCategoryData: any;
  editedBoolen: any;
  setEditSuccess: any;
  filteredRowData: any;
}

const AddCategoryModal = ({
  handleClose,
  setOpenSnackbar,
  getCategory,
  editCategoryData,
  editedBoolen,
  setEditSuccess,
  filteredRowData,
}: IProps) => {
  const dispatch = useAppDispatch();
  const [apiError, setApiError] = useState(false);
  const [manufacturerId, setManufacturerId] = useState(
    editCategoryData?.super_category ? editCategoryData?.super_category : 0
  );
  const [categoryName, setCategoryName] = useState("");
  const [categoryNameError, setCategoryNameError] = useState("");
  const [selectedType, setSelectedType] = useState({
    label: "Medical",
    value: 0,
  });

  const [loading, setLoading] = useState(false);
  const handleInputChange1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategoryName(e.target.value);
  };

  useEffect(() => {
    if (editCategoryData) {
      const newType = typeOptions.find(
        (option) => option.value === editCategoryData.super_category
      );
      if (newType) {
        setSelectedType(newType);
      }
      setCategoryName(editCategoryData?.name);
    }
  }, [editCategoryData]);

  const handleSubmit = async (values: any) => {
    // setLoading(true);

    if (!categoryName && (!editedBoolen || editedBoolen)) {
      return setCategoryNameError("Required *");
    } else {
      setCategoryNameError("");
    }

    setLoading(true);
    const newName = categoryName?.trim(); // Keep the exact casing for saving
    const normalizedNewName = newName?.toLowerCase();

    // Check for duplicates in filteredRowData (case-insensitive)
    const isDuplicate =
      !editedBoolen &&
      filteredRowData?.some(
        (category: any) =>
          category?.name?.trim().toLowerCase() === normalizedNewName &&
          category?.super_category === selectedType.value
      );

    if (isDuplicate) {
      setApiError(true);
      console.error("Category with this name already exists.");
      setLoading(false); // Stop the loader
      return; // Prevent API call on duplicate
    }

    if (editedBoolen) {
      const obj = {
        category_name: categoryName,
        super_category: manufacturerId,
        category_id: editCategoryData?.id,
      };

      try {
        const res = await dispatch(EditedCategory(obj));
        if (res?.payload?.success === true) {
          getCategory();
          setOpenSnackbar();
          handleClose();
          setEditSuccess(true);
        } else {
          setApiError(true);
        }
      } catch (error) {
        setApiError(true);
        console.error("Error edited Category: ", error);
      } finally {
        setLoading(false); // Stop the loader
      }
    } else {
      const obj = {
        category_name: categoryName,
        super_category: manufacturerId,
      };

      try {
        const res = await dispatch(AddCategory(obj));
        if (res?.payload?.success === true) {
          getCategory();
          setOpenSnackbar();
          handleClose();
        } else {
          setApiError(true);
        }
      } catch (error) {
        setApiError(true);
        console.error("Error adding Category: ", error);
      } finally {
        setLoading(false); // Stop the loader
      }
    }
  };

  return (
    <Formik
      initialValues={addCategoryInitialValues}
      // validationSchema={!editedBoolen && addcategoryValidationSchema}
      onSubmit={handleSubmit}
    >
      <Form>
        <Box sx={{ mt: 0.5 }}>
          <Grid  container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Type
              </Typography>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={typeOptions}
                getOptionLabel={(option) => option.label}
                value={selectedType}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setSelectedType(newValue);
                    setManufacturerId(newValue.value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type"
                    fullWidth
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "1px 5px !important",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                Category Name
              </Typography>

              <Field
                as={TextField}
                fullWidth
                variant="outlined"
                name="name"
                value={categoryName}
                onChange={handleInputChange1}
                inputProps={{ style: { padding: "10px 12px" } }}
              />
              {categoryNameError && (
                <div
                  style={{
                    color: "red",
                    fontSize: "14px",
                    marginTop: "5px",
                    marginLeft: 2,
                  }}
                >
                  {categoryNameError}
                </div>
              )}

              {apiError && (
                <div
                  style={{
                    color: apiError ? "red" : "red",
                    fontSize: "14px",
                    marginTop: "5px",
                  }}
                >
                  {" "}
                  Category with this name already exists{" "}
                </div>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <AppButton
            variant="contained"
            color="primary"
            type="submit"
            title={
              loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : editedBoolen ? (
                "Update"
              ) : (
                "Add"
              )
            }
            sx={{ float: "right" }}
            disabled={loading} 
          />
        </Box>
      </Form>
    </Formik>
  );
};

export default AddCategoryModal;
